import React, { useEffect, useState, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { formatDate, formatEUR } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Trash2, Download, Clock, ClipboardCheck, Calendar, FileSpreadsheet, ChevronLeft, ChevronRight, Boxes } from "lucide-react";

const INPUT_CSS = `.input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));padding:.5rem .75rem;font-size:.875rem;outline:none}.input:focus{border-color:var(--brand)}`;

// ==================== PRINTER CALENDAR ====================
export function AdminSchedule() {
  const [items, setItems] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [showNew, setShowNew] = useState(false);
  const [n, setN] = useState({ printer_id: "", job_id: "", title: "", start: "", end: "", notes: "" });

  const reload = () => {
    const from = weekStart.toISOString().slice(0, 10);
    const to = new Date(weekStart.getTime() + 7 * 86400000).toISOString().slice(0, 10);
    api.get("/schedule", { params: { from_date: from, to_date: to + "T23:59" } }).then((r) => setItems(r.data));
    api.get("/production/printers").then((r) => setPrinters(r.data));
  };
  useEffect(() => { reload(); }, [weekStart]);

  const days = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => new Date(weekStart.getTime() + i * 86400000));
  }, [weekStart]);

  async function create() {
    if (!n.printer_id || !n.title || !n.start || !n.end) return toast.error("Preencha campos obrigatórios");
    try { await api.post("/schedule", n); toast.success("Agendamento criado"); setShowNew(false); reload(); }
    catch (e) { toast.error(apiError(e)); }
  }
  async function del(id) {
    if (!confirm("Eliminar agendamento?")) return;
    await api.delete(`/schedule/${id}`); reload();
  }

  return (
    <div className="anim-in">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tighter">Planeamento · Calendário</h1>
          <p className="tech-label mt-1">Semana de {formatDate(weekStart.toISOString())} a {formatDate(new Date(weekStart.getTime() + 6 * 86400000).toISOString())}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * 86400000))} className="btn-outline text-xs" data-testid="calendar-prev"><ChevronLeft size={12} /></button>
          <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="btn-outline text-xs">Hoje</button>
          <button onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * 86400000))} className="btn-outline text-xs" data-testid="calendar-next"><ChevronRight size={12} /></button>
          <button onClick={() => setShowNew(true)} className="btn-primary text-xs" data-testid="calendar-add"><Plus size={12} /> Novo</button>
        </div>
      </div>

      <div className="border border-border overflow-x-auto bg-card">
        <div className="grid" style={{ gridTemplateColumns: `220px repeat(7, minmax(140px, 1fr))` }}>
          {/* Header row */}
          <div className="p-3 border-b border-border tech-label">Impressora</div>
          {days.map((d, i) => (
            <div key={i} className={`p-3 border-b border-l border-border ${sameDay(d, new Date()) ? "bg-brand/5" : ""}`}>
              <p className="mono text-[10px] uppercase text-muted-foreground">{d.toLocaleDateString("pt-PT", { weekday: "short" })}</p>
              <p className="font-semibold">{d.getDate()}/{(d.getMonth() + 1).toString().padStart(2, "0")}</p>
            </div>
          ))}

          {/* Printer rows */}
          {printers.map((pr) => (
            <React.Fragment key={pr.printer_id}>
              <div className="p-3 border-b border-border">
                <p className="font-semibold text-sm">{pr.name}</p>
                <div className="mt-1"><StatusBadge status={pr.status} label={pr.status} /></div>
              </div>
              {days.map((d, i) => {
                const dayItems = items.filter((it) => it.printer_id === pr.printer_id && sameDay(new Date(it.start), d));
                return (
                  <div key={i} className={`p-2 border-b border-l border-border min-h-[80px] ${sameDay(d, new Date()) ? "bg-brand/5" : ""}`}>
                    {dayItems.map((it) => (
                      <div key={it.schedule_id} className="text-xs border border-border bg-background p-2 mb-1 group relative">
                        <p className="font-medium truncate">{it.title}</p>
                        <p className="mono text-[9px] text-muted-foreground uppercase">
                          {new Date(it.start).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })} — {new Date(it.end).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                        <button onClick={() => del(it.schedule_id)} className="opacity-0 group-hover:opacity-100 absolute top-1 right-1 text-muted-foreground hover:text-destructive" data-testid={`calendar-del-${it.schedule_id}`}><Trash2 size={10} /></button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {showNew && (
        <Modal onClose={() => setShowNew(false)} title="Novo agendamento">
          <div className="space-y-3">
            <select className="input" value={n.printer_id} onChange={(e) => setN({ ...n, printer_id: e.target.value })} data-testid="calendar-new-printer">
              <option value="">— Impressora —</option>
              {printers.map((p) => <option key={p.printer_id} value={p.printer_id}>{p.name}</option>)}
            </select>
            <input className="input" placeholder="Título" value={n.title} onChange={(e) => setN({ ...n, title: e.target.value })} data-testid="calendar-new-title" />
            <label className="tech-label">Início</label>
            <input type="datetime-local" className="input" value={n.start} onChange={(e) => setN({ ...n, start: e.target.value })} data-testid="calendar-new-start" />
            <label className="tech-label">Fim</label>
            <input type="datetime-local" className="input" value={n.end} onChange={(e) => setN({ ...n, end: e.target.value })} data-testid="calendar-new-end" />
            <textarea className="input" placeholder="Notas" rows={2} value={n.notes} onChange={(e) => setN({ ...n, notes: e.target.value })} />
          </div>
          <div className="flex gap-2 mt-4"><button className="btn-primary text-sm" onClick={create} data-testid="calendar-new-save">Criar</button><button className="btn-outline text-sm" onClick={() => setShowNew(false)}>Cancelar</button></div>
        </Modal>
      )}
      <style>{INPUT_CSS}</style>
    </div>
  );
}

function startOfWeek(d) {
  const n = new Date(d);
  const day = n.getDay() || 7;
  n.setHours(0, 0, 0, 0);
  n.setDate(n.getDate() - day + 1);
  return n;
}
function sameDay(a, b) { return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear(); }

// ==================== TIME TRACKING ====================
export function AdminTime() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [t, setT] = useState({ activity: "modeling", description: "", minutes: 30, project_id: "", order_id: "", hourly_cost: 20 });
  const reload = () => api.get("/time-entries").then((r) => setItems(r.data));
  useEffect(() => { reload(); }, []);
  async function create() {
    if (!t.minutes || !t.activity) return toast.error("Preencha campos");
    try { await api.post("/time-entries", t); toast.success("Registo criado"); setShowNew(false); reload(); }
    catch (e) { toast.error(apiError(e)); }
  }
  const total = items.reduce((s, i) => s + i.minutes, 0);
  const totalCost = items.reduce((s, i) => s + (i.cost || 0), 0);

  return (
    <div className="anim-in">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold tracking-tighter">Registo de horas</h1>
        <button onClick={() => setShowNew(true)} className="btn-primary text-xs" data-testid="time-add"><Plus size={12} /> Novo registo</button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="border border-border p-4 bg-card"><p className="tech-label">Total horas</p><p className="text-3xl font-bold mt-2">{(total / 60).toFixed(1)}h</p></div>
        <div className="border border-border p-4 bg-card"><p className="tech-label">Custo interno</p><p className="text-3xl font-bold mt-2">{formatEUR(totalCost)}</p></div>
        <div className="border border-border p-4 bg-card"><p className="tech-label">Registos</p><p className="text-3xl font-bold mt-2">{items.length}</p></div>
      </div>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left">
            <th className="p-3 tech-label">Data</th><th className="p-3 tech-label">Utilizador</th><th className="p-3 tech-label">Atividade</th>
            <th className="p-3 tech-label">Descrição</th><th className="p-3 tech-label">Horas</th><th className="p-3 tech-label">Custo</th>
          </tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.entry_id} className="border-t border-border">
                <td className="p-3 mono text-xs">{formatDate(i.date)}</td>
                <td className="p-3">{i.user_name}</td>
                <td className="p-3 mono text-xs uppercase">{i.activity}</td>
                <td className="p-3 text-muted-foreground text-xs">{i.description}</td>
                <td className="p-3 mono">{(i.minutes / 60).toFixed(2)}h</td>
                <td className="p-3 font-medium">{formatEUR(i.cost || 0)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 tech-label text-center">sem registos</td></tr>}
          </tbody>
        </table>
      </div>
      {showNew && (
        <Modal onClose={() => setShowNew(false)} title="Novo registo de tempo">
          <div className="space-y-3">
            <select className="input" value={t.activity} onChange={(e) => setT({ ...t, activity: e.target.value })} data-testid="time-activity">
              {["analysis","modeling","preparation","printing","finishing","assembly","quality","packaging","shipping","meeting","other"].map(a => <option key={a}>{a}</option>)}
            </select>
            <input className="input" placeholder="Descrição" value={t.description} onChange={(e) => setT({ ...t, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <input type="number" className="input" placeholder="Minutos" value={t.minutes} onChange={(e) => setT({ ...t, minutes: Number(e.target.value) })} data-testid="time-minutes" />
              <input type="number" step="0.01" className="input" placeholder="€/hora" value={t.hourly_cost} onChange={(e) => setT({ ...t, hourly_cost: Number(e.target.value) })} />
            </div>
            <input className="input" placeholder="ID de encomenda (opcional)" value={t.order_id} onChange={(e) => setT({ ...t, order_id: e.target.value })} />
          </div>
          <div className="flex gap-2 mt-4"><button className="btn-primary text-sm" onClick={create} data-testid="time-save">Criar</button><button className="btn-outline text-sm" onClick={() => setShowNew(false)}>Cancelar</button></div>
        </Modal>
      )}
      <style>{INPUT_CSS}</style>
    </div>
  );
}

// ==================== QUALITY CONTROL ====================
export function AdminQuality() {
  const [reports, setReports] = useState([]);
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [overall, setOverall] = useState("approved");
  const [notes, setNotes] = useState("");
  const [checks, setChecks] = useState([]);

  const reload = () => api.get("/qc/reports").then((r) => setReports(r.data));
  useEffect(() => {
    reload();
    api.get("/qc/templates").then((r) => setItems(r.data.default_items || []));
  }, []);

  useEffect(() => { if (items.length && !checks.length) setChecks(items.map((n) => ({ name: n, result: "ok", note: "" }))); }, [items]);

  async function create() {
    try {
      await api.post("/qc/reports", { order_id: orderId || null, items: checks, overall, notes });
      toast.success("Relatório de qualidade criado");
      setShowNew(false); reload();
      setChecks(items.map((n) => ({ name: n, result: "ok", note: "" })));
    } catch (e) { toast.error(apiError(e)); }
  }

  return (
    <div className="anim-in">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold tracking-tighter">Controlo de qualidade</h1>
        <button onClick={() => setShowNew(true)} className="btn-primary text-xs" data-testid="qc-add"><ClipboardCheck size={12} /> Novo relatório</button>
      </div>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left">
            <th className="p-3 tech-label">Data</th><th className="p-3 tech-label">Responsável</th><th className="p-3 tech-label">Encomenda</th>
            <th className="p-3 tech-label">Resultado</th><th className="p-3 tech-label">Notas</th>
          </tr></thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.qc_id} className="border-t border-border">
                <td className="p-3 mono text-xs">{formatDate(r.created_at)}</td>
                <td className="p-3">{r.responsible}</td>
                <td className="p-3 mono text-xs">{r.order_id || "—"}</td>
                <td className="p-3"><StatusBadge status={r.overall} label={r.overall} /></td>
                <td className="p-3 text-xs text-muted-foreground truncate max-w-xs">{r.notes || "—"}</td>
              </tr>
            ))}
            {reports.length === 0 && <tr><td colSpan={5} className="p-8 tech-label text-center">sem relatórios</td></tr>}
          </tbody>
        </table>
      </div>
      {showNew && (
        <Modal onClose={() => setShowNew(false)} title="Relatório de qualidade" wide>
          <div className="space-y-3">
            <input className="input" placeholder="ID de encomenda (opcional)" value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="qc-order-id" />
            <div className="border border-border max-h-64 overflow-y-auto">
              {checks.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center p-2 border-b border-border last:border-0">
                  <span className="col-span-4 text-sm">{c.name}</span>
                  <select className="input col-span-3" value={c.result} onChange={(e) => setChecks((cs) => cs.map((x, j) => j === i ? { ...x, result: e.target.value } : x))}>
                    <option value="ok">OK</option><option value="observation">Observação</option>
                    <option value="rework">Retrabalho</option><option value="fail">Falha</option>
                  </select>
                  <input className="input col-span-5" placeholder="Notas" value={c.note} onChange={(e) => setChecks((cs) => cs.map((x, j) => j === i ? { ...x, note: e.target.value } : x))} />
                </div>
              ))}
            </div>
            <div>
              <label className="tech-label">Resultado global</label>
              <select className="input mt-1" value={overall} onChange={(e) => setOverall(e.target.value)} data-testid="qc-overall">
                <option value="approved">Aprovado</option><option value="approved_with_note">Aprovado com observação</option>
                <option value="rework">Retrabalho</option><option value="reprint">Reimpressão</option><option value="rejected">Reprovado</option>
              </select>
            </div>
            <textarea className="input" rows={2} placeholder="Notas gerais" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="flex gap-2 mt-4"><button className="btn-primary text-sm" onClick={create} data-testid="qc-save">Guardar</button><button className="btn-outline text-sm" onClick={() => setShowNew(false)}>Cancelar</button></div>
        </Modal>
      )}
      <style>{INPUT_CSS}</style>
    </div>
  );
}

// ==================== MATERIAL CONSUMPTION ====================
export function AdminConsumption() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [c, setC] = useState({ job_id: "", material_id: "", weight_g: 0, weight_purge_g: 0, weight_waste_g: 0, note: "" });
  const reload = () => {
    api.get("/consumption").then((r) => setItems(r.data));
    api.get("/production/materials").then((r) => setMaterials(r.data));
    api.get("/production/jobs").then((r) => setJobs(r.data));
  };
  useEffect(() => { reload(); }, []);
  async function create() {
    if (!c.job_id || !c.material_id || !c.weight_g) return toast.error("Preencha campos obrigatórios");
    try { await api.post("/consumption", c); toast.success("Consumo registado"); setShowNew(false); reload(); }
    catch (e) { toast.error(apiError(e)); }
  }
  const totalG = items.reduce((s, i) => s + i.weight_g, 0);
  const totalCost = items.reduce((s, i) => s + (i.cost || 0), 0);

  return (
    <div className="anim-in">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold tracking-tighter">Consumos de material</h1>
        <button onClick={() => setShowNew(true)} className="btn-primary text-xs" data-testid="cons-add"><Plus size={12} /> Novo consumo</button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="border border-border p-4 bg-card"><p className="tech-label">Total consumido</p><p className="text-3xl font-bold mt-2">{(totalG / 1000).toFixed(2)} kg</p></div>
        <div className="border border-border p-4 bg-card"><p className="tech-label">Custo material</p><p className="text-3xl font-bold mt-2">{formatEUR(totalCost)}</p></div>
        <div className="border border-border p-4 bg-card"><p className="tech-label">Registos</p><p className="text-3xl font-bold mt-2">{items.length}</p></div>
      </div>
      <div className="border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary"><tr className="text-left">
            <th className="p-3 tech-label">Data</th><th className="p-3 tech-label">Trabalho</th><th className="p-3 tech-label">Material</th>
            <th className="p-3 tech-label">Peso</th><th className="p-3 tech-label">Desperdício</th><th className="p-3 tech-label">Custo</th>
          </tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.consumption_id} className="border-t border-border">
                <td className="p-3 mono text-xs">{formatDate(i.created_at)}</td>
                <td className="p-3 mono text-xs">{i.job_id?.slice(-8) || "—"}</td>
                <td className="p-3">{i.material_name}</td>
                <td className="p-3 mono">{i.weight_g}g</td>
                <td className="p-3 mono text-xs text-muted-foreground">{(i.weight_purge_g || 0) + (i.weight_waste_g || 0)}g</td>
                <td className="p-3 font-medium">{formatEUR(i.cost || 0)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 tech-label text-center">sem consumos</td></tr>}
          </tbody>
        </table>
      </div>
      {showNew && (
        <Modal onClose={() => setShowNew(false)} title="Registar consumo">
          <div className="space-y-3">
            <select className="input" value={c.job_id} onChange={(e) => setC({ ...c, job_id: e.target.value })} data-testid="cons-job">
              <option value="">— Trabalho —</option>
              {jobs.map((j) => <option key={j.job_id} value={j.job_id}>{j.product_name} · {j.job_id.slice(-6)}</option>)}
            </select>
            <select className="input" value={c.material_id} onChange={(e) => setC({ ...c, material_id: e.target.value })} data-testid="cons-material">
              <option value="">— Material —</option>
              {materials.map((m) => <option key={m.material_id} value={m.material_id}>{m.name} · {m.weight_available_g}g disponíveis</option>)}
            </select>
            <div className="grid grid-cols-3 gap-2">
              <input type="number" step="0.1" className="input" placeholder="Peso (g)" value={c.weight_g} onChange={(e) => setC({ ...c, weight_g: Number(e.target.value) })} data-testid="cons-weight" />
              <input type="number" step="0.1" className="input" placeholder="Purga (g)" value={c.weight_purge_g} onChange={(e) => setC({ ...c, weight_purge_g: Number(e.target.value) })} />
              <input type="number" step="0.1" className="input" placeholder="Desperdício (g)" value={c.weight_waste_g} onChange={(e) => setC({ ...c, weight_waste_g: Number(e.target.value) })} />
            </div>
            <input className="input" placeholder="Notas" value={c.note} onChange={(e) => setC({ ...c, note: e.target.value })} />
          </div>
          <div className="flex gap-2 mt-4"><button className="btn-primary text-sm" onClick={create} data-testid="cons-save">Registar</button><button className="btn-outline text-sm" onClick={() => setShowNew(false)}>Cancelar</button></div>
        </Modal>
      )}
      <style>{INPUT_CSS}</style>
    </div>
  );
}

// ==================== EXPORTS ====================
export function AdminExports() {
  const ENTITIES = [
    { id: "orders", label: "Encomendas", icon: Boxes, desc: "Todas as encomendas com totais e estados." },
    { id: "quotes", label: "Orçamentos", icon: FileSpreadsheet, desc: "Pedidos de orçamento recebidos." },
    { id: "proposals", label: "Propostas", icon: FileSpreadsheet, desc: "Propostas comerciais emitidas." },
    { id: "customers", label: "Clientes", icon: FileSpreadsheet, desc: "Base de dados de clientes." },
    { id: "jobs", label: "Trabalhos", icon: Boxes, desc: "Trabalhos de impressão." },
    { id: "materials", label: "Materiais", icon: Boxes, desc: "Inventário de materiais." },
    { id: "time_entries", label: "Registos de horas", icon: Clock, desc: "Horas registadas por utilizador." },
  ];
  async function download(entity) {
    try {
      const r = await api.get(`/admin/export/${entity}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cadditive_${entity}_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportado: ${entity}`);
    } catch (e) { toast.error("Erro na exportação"); }
  }
  return (
    <div className="anim-in">
      <h1 className="text-3xl font-bold tracking-tighter mb-2">Relatórios · Exportações</h1>
      <p className="tech-label mb-8">CSV compatível com Excel · encoding UTF-8</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ENTITIES.map((e) => (
          <div key={e.id} className="border border-border p-5 bg-card hover:border-foreground">
            <div className="flex items-start justify-between">
              <e.icon size={22} className="text-brand" />
              <button onClick={() => download(e.id)} className="btn-primary text-xs" data-testid={`export-${e.id}`}><Download size={12} /> CSV</button>
            </div>
            <h3 className="text-xl font-bold mt-4 tracking-tight">{e.label}</h3>
            <p className="text-xs text-muted-foreground mt-1">{e.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== PLACEHOLDERS Fase 3 ====================
export function AdminIntegrations() {
  return (
    <div className="anim-in max-w-3xl">
      <h1 className="text-3xl font-bold tracking-tighter mb-2">Integrações · Fase 3</h1>
      <p className="tech-label mb-8">Módulos preparados na arquitetura · ativação mediante credenciais</p>
      <div className="space-y-3">
        <Integration title="Bambu Lab Cloud" status="preparado" desc="Leitura em tempo real do estado das impressoras. Requer credenciais Bambu Lab Cloud." />
        <Integration title="Pagamentos online (Stripe)" status="preparado" desc="Chave de teste Stripe disponível no ambiente. Requer configuração de webhook e certificação de comerciante." />
        <Integration title="Pagamentos online MB WAY" status="preparado" desc="Requer contrato com gateway certificado (SIBS, Ifthenpay, etc.)." />
        <Integration title="Faturação SAF-T (Portugal)" status="preparado" desc="Emissão de faturas certificadas requer software homologado pela AT. Preparado para integração com Moloni/InvoiceXpress/Vendus." />
        <Integration title="WhatsApp Business API" status="pendente" desc="Requer credenciais Meta ou Twilio. Sistema regista notificações como pendentes até configuração." />
        <Integration title="Multilingue (EN/FR/ES)" status="preparado" desc="Estrutura de traduções pronta em /lib/format.js. Adição de novos idiomas via ficheiros locale." />
      </div>
    </div>
  );
}
const Integration = ({ title, status, desc }) => (
  <div className="border border-border p-5 bg-card flex items-start justify-between gap-4">
    <div><h3 className="font-semibold">{title}</h3><p className="text-xs text-muted-foreground mt-1">{desc}</p></div>
    <StatusBadge status={status} label={status} />
  </div>
);

// ==================== Modal helper ====================
function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-card border border-border w-full p-6 ${wide ? "max-w-2xl" : "max-w-md"}`} onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}
